import useEmblaCarousel from 'embla-carousel-react';
import {
  Children,
  type CSSProperties,
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

type EmblaOptions = Parameters<typeof useEmblaCarousel>[0];
type EmblaApi = NonNullable<ReturnType<typeof useEmblaCarousel>[1]>;

type ArrowVariant = 'arrow' | 'angle';

export type EmblaCarouselProps = {
  arrows?: boolean;
  arrowVariant?: ArrowVariant;
  autoplay?: boolean;
  autoplayInterval?: number;
  children: ReactNode;
  className?: string;
  dots?: boolean;
  options?: EmblaOptions;
  slideClassName?: string;
  style?: CSSProperties;
  viewportClassName?: string;
};

const classes = (...classNames: Array<string | false | undefined>) =>
  classNames.filter(Boolean).join(' ');

export const EmblaCarousel: FC<EmblaCarouselProps> = ({
  arrows = false,
  arrowVariant = 'arrow',
  autoplay = false,
  autoplayInterval = 3000,
  children,
  className,
  dots = false,
  options,
  slideClassName,
  style,
  viewportClassName,
}) => {
  const slides = useMemo(() => Children.toArray(children), [children]);
  const [emblaRef, emblaApi] = useEmblaCarousel(options);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const [prevButtonDisabled, setPrevButtonDisabled] = useState(true);
  const [nextButtonDisabled, setNextButtonDisabled] = useState(true);

  const updateCarouselState = useCallback((api: EmblaApi) => {
    setSelectedIndex(api.selectedScrollSnap());
    setScrollSnaps(api.scrollSnapList());
    setPrevButtonDisabled(!api.canScrollPrev());
    setNextButtonDisabled(!api.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    updateCarouselState(emblaApi);
    emblaApi.on('reInit', updateCarouselState);
    emblaApi.on('select', updateCarouselState);

    return () => {
      emblaApi.off('reInit', updateCarouselState);
      emblaApi.off('select', updateCarouselState);
    };
  }, [emblaApi, updateCarouselState]);

  useEffect(() => {
    if (!autoplay || !emblaApi || slides.length < 2) return;

    const intervalId = window.setInterval(() => {
      if (!document.hidden) {
        emblaApi.scrollNext();
      }
    }, autoplayInterval);

    return () => window.clearInterval(intervalId);
  }, [autoplay, autoplayInterval, emblaApi, slides.length]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback(
    (index: number) => emblaApi?.scrollTo(index),
    [emblaApi],
  );

  const leftIcon =
    arrowVariant === 'angle' ? 'fas fa-angle-left' : 'fas fa-arrow-left';
  const rightIcon =
    arrowVariant === 'angle' ? 'fas fa-angle-right' : 'fas fa-arrow-right';

  return (
    <div className={classes('embla-carousel', className)} style={style}>
      <div
        className={classes('embla-carousel__viewport', viewportClassName)}
        ref={emblaRef}
      >
        <div className='embla-carousel__container'>
          {slides.map((slide, index) => (
            <div
              className={classes(
                'embla-carousel__slide slick-slide',
                selectedIndex === index &&
                  'embla-carousel__slide--selected slick-current',
                slideClassName,
              )}
              key={index}
            >
              {slide}
            </div>
          ))}
        </div>
      </div>

      {arrows && slides.length > 1 && (
        <>
          <button
            aria-disabled={prevButtonDisabled}
            aria-label='Previous slide'
            className={classes(
              'slick-prev slick-arrow',
              prevButtonDisabled && 'slick-disabled',
            )}
            onClick={scrollPrev}
            type='button'
          >
            <i className={leftIcon}></i>
          </button>
          <button
            aria-disabled={nextButtonDisabled}
            aria-label='Next slide'
            className={classes(
              'slick-next slick-arrow',
              nextButtonDisabled && 'slick-disabled',
            )}
            onClick={scrollNext}
            type='button'
          >
            <i className={rightIcon}></i>
          </button>
        </>
      )}

      {dots && scrollSnaps.length > 1 && (
        <ul className='slick-dots'>
          {scrollSnaps.map((_, index) => (
            <li
              className={classes(index === selectedIndex && 'slick-active')}
              key={index}
            >
              <button
                aria-label={`Go to slide ${index + 1}`}
                onClick={() => scrollTo(index)}
                type='button'
              ></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
